import {
  calendarDay,
  derivedTasks,
  isDue,
  ownsJob,
  taskBadgeCount,
  type DerivedTask,
  type TaskJob,
  type TaskRow,
} from "@roof-hawk/core";
import { useEffect, useMemo, useRef, useState } from "react";

import { CHECK_ICON } from "../../components/ui";
import { t } from "../../i18n";
import { navigate, rememberValue, useAsync } from "../../lib/hooks";
import {
  answerDerivedTask,
  assignReviewedText,
  fetchMyTasks,
  fetchSupplierMailSignals,
  fetchTaskSignals,
  fetchTextReviewChoices,
  insertRow,
  updateRow,
  type TextReviewChoice,
} from "../../lib/queries";
import { useSession } from "../../session";

/**
 * What you owe, in the corner of every screen.
 *
 * Jonathon, 2026-09-02: "always at the top right. accessible but doesn't block
 * any view. need to be able to click on it, drops down, and then we can hide
 * it."
 *
 * Almost nothing in here was typed by anybody. `derivedTasks` in packages/core
 * reads the jobs the app has already fetched plus five narrow queries and works
 * out what is owed — a customer nobody has rung in two days, a proposal opened
 * twice and never signed, a production job with no crew booked. A rep can add
 * their own on top, and assign one to anybody.
 *
 * Three decisions worth keeping:
 *
 *   1. **The badge counts only what is due today or overdue.** The whole list
 *      would be a number that never goes down, and a badge nobody can clear is
 *      a badge people stop seeing.
 *   2. **Ticking a computed item writes a row.** Ticking "chase this proposal"
 *      does not sign the proposal, so without a record it would be back on the
 *      next render. `answerDerivedTask` stores the answer against the item's
 *      key.
 *   3. **Done today stays on screen.** Ticking something and watching it vanish
 *      without trace is how a list stops being worth keeping.
 */
export function TaskList({ jobs }: { jobs: readonly TaskJob[] }) {
  const { profileId, people } = useSession();
  const [open, setOpen] = useState(false);
  const [adding, setAdding] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const closing = useRef<number | undefined>(undefined);
  // Leaving the list closes it 200 ms later. If the list itself goes away
  // inside that window, the timer must go with it rather than fire into a
  // screen that is no longer there — on CI it fired after the test page was
  // torn down and failed a build whose every test had passed. The ref, not its
  // value, is captured: it is the timer running at unmount that must stop.
  useEffect(() => {
    const timer = closing;
    return () => window.clearTimeout(timer.current);
  }, []);

  const mine = useAsync(
    () => (profileId ? fetchMyTasks(profileId) : Promise.resolve([])),
    [profileId],
  );
  /**
   * The jobs whose signals are worth fetching: this person's own.
   *
   * `derivedTasks` discards every proposal, order and invoice belonging to
   * anybody else, so asking the database for the company's is both waste and a
   * trap — see `fetchTaskSignals`, where a truncated answer turns into a
   * "nobody is booked" task on a job that is booked. `ownsJob` is the same
   * test the rule uses, so the two cannot drift apart.
   */
  const myJobIds = useMemo(
    () =>
      profileId
        ? jobs.filter((job) => ownsJob(job, profileId)).map((job) => job.id)
        : [],
    [jobs, profileId],
  );

  const signals = useAsync(
    () =>
      profileId
        ? fetchTaskSignals(profileId, myJobIds)
        : Promise.resolve({
            proposals: [],
            mentions: [],
            materialOrders: [],
            workOrders: [],
            materialSteps: [],
            invoices: [],
            readings: [],
            outbound: [],
            calls: [],
            payments: [],
            receipts: [],
            latePlans: [],
            unsignedWork: [],
            termsChecks: [],
          }),
    [profileId, myJobIds],
  );
  // Supplier mail nobody could place is everybody's, jobs or no jobs
  // (ADR 0311), so it is asked for whether or not this person owns a job.
  const supplierMail = useAsync(
    () =>
      profileId
        ? fetchSupplierMailSignals()
        : Promise.resolve({ supplierMail: [], materialReturns: [] }),
    [profileId],
  );

  const rows = useMemo(() => mine.data ?? [], [mine.data]);
  const reviews = useAsync(
    () =>
      fetchTextReviewChoices(
        rows.filter((row) => !row.done_at).map((row) => row.id),
      ),
    [rows],
  );
  const today = localToday();

  const derived = useMemo(
    () =>
      profileId
        ? derivedTasks(
            {
              profileId,
              jobs,
              rows,
              ...(signals.data ?? {}),
              ...(supplierMail.data ?? {}),
            },
            new Date(),
          )
        : [],
    [profileId, jobs, rows, signals.data, supplierMail.data],
  );

  /** The tasks somebody typed, still open. */
  const written = useMemo(
    () =>
      rows
        .filter(
          (row) =>
            !row.source_key &&
            !row.done_at &&
            (!row.snoozed_until || row.snoozed_until <= today),
        )
        .sort((a, b) => (a.due_on ?? "9999").localeCompare(b.due_on ?? "9999")),
    [rows, today],
  );

  // `calendarDay`, not the first ten characters of the timestamp. `done_at` is
  // UTC, so after five in the afternoon here its date string is already
  // tomorrow's — a task ticked at the end of the day would drop straight out of
  // "Done today", which is the one moment somebody wants to see it.
  const doneToday = useMemo(
    () => rows.filter((row) => calendarDay(row.done_at) === today),
    [rows, today],
  );

  const count = taskBadgeCount(derived, rows, today);

  async function reload() {
    await Promise.all([
      mine.reload(),
      signals.reload(),
      supplierMail.reload(),
      reviews.reload(),
    ]);
  }

  async function assignText(taskId: string, jobId: string) {
    setError(null);
    try {
      await assignReviewedText(taskId, jobId);
      await reload();
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    }
  }

  /** Tick a computed item, or put it off. */
  async function answer(task: DerivedTask, snoozeUntil: string | null) {
    if (!profileId) return;
    setError(null);
    try {
      await answerDerivedTask({ task, profileId, snoozeUntil });
      await mine.reload();
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    }
  }

  /** Tick one somebody typed. Painted first, put back if the write fails. */
  async function tickWritten(row: TaskRow) {
    if (!profileId) return;
    setError(null);
    const at = new Date().toISOString();
    mine.setData((current) =>
      (current ?? []).map((one) =>
        one.id === row.id ? { ...one, done_at: at, done_by: profileId } : one,
      ),
    );
    try {
      await updateRow("tasks", row.id, { done_at: at, done_by: profileId });
    } catch (e) {
      mine.setData((current) =>
        (current ?? []).map((one) =>
          one.id === row.id ? { ...one, done_at: null, done_by: null } : one,
        ),
      );
      setError(e instanceof Error ? e.message : String(e));
    }
  }

  async function untick(row: TaskRow) {
    setError(null);
    mine.setData((current) =>
      (current ?? []).map((one) =>
        one.id === row.id ? { ...one, done_at: null, done_by: null } : one,
      ),
    );
    try {
      await updateRow("tasks", row.id, { done_at: null, done_by: null });
    } catch {
      await mine.reload();
    }
  }

  function goTo(jobId: string | null) {
    if (!jobId) return;
    setOpen(false);
    navigate(`job=${jobId}`);
  }

  /**
   * Where a computed task opens. A job's, on the job; a supplier email with
   * no job yet, on the Tools screen that places it (ADR 0311).
   */
  function openDerived(task: DerivedTask) {
    if (task.kind === "supplier_mail_review" && !task.jobId) {
      setOpen(false);
      rememberValue("tools", "Supplier Mail");
      navigate("tools");
      return;
    }
    goTo(task.jobId);
  }

  function openWritten(row: TaskRow) {
    const review = reviews.data?.find((one) => one.taskId === row.id);
    if (review?.contactId) {
      setOpen(false);
      navigate(`contact=${review.contactId}`);
    } else {
      goTo(row.job_id);
    }
  }

  const dueNow = derived.filter((task) => isDue(task, today));
  const later = derived.filter((task) => !isDue(task, today));
  const writtenDue = written.filter((row) =>
    isDue({ dueOn: row.due_on }, today),
  );
  const writtenLater = written.filter(
    (row) => !isDue({ dueOn: row.due_on }, today),
  );
  const nothing =
    derived.length === 0 && written.length === 0 && doneToday.length === 0;

  return (
    <div
      className="tasks"
      onBlur={() => {
        closing.current = window.setTimeout(() => setOpen(false), 200);
      }}
      onFocus={() => window.clearTimeout(closing.current)}
    >
      <button
        className={`taskbtn${count > 0 ? " due" : ""}`}
        aria-label={t("My list, {n} due", { n: count })}
        aria-expanded={open}
        onClick={() => setOpen((was) => !was)}
      >
        <svg viewBox="0 0 24 24" fill="none" strokeWidth="1.9">
          <path d="M9 5h9M9 12h9M9 19h9" />
          <path d="M4 5.5 5 6.5 7 4.5" />
          <path d="M4 12.5 5 13.5 7 11.5" />
          <path d="M4 19.5 5 20.5 7 18.5" />
        </svg>
        {count > 0 && <span className="taskbadge">{count}</span>}
      </button>

      {open && (
        <div className="ac-drop taskdrop">
          <div className="taskhead">
            <b>{t("My list")}</b>
            <span className="n">
              {count > 0 ? t("{n} due", { n: count }) : t("nothing due")}
            </span>
            <button
              className="linkbtn"
              onClick={() => setAdding((was) => !was)}
            >
              {adding ? t("Cancel") : t("+ Add")}
            </button>
          </div>

          {error && (
            <div className="err" role="alert">
              {error}
            </div>
          )}

          {adding && (
            <AddTask
              people={people}
              profileId={profileId}
              onDone={async () => {
                setAdding(false);
                await reload();
              }}
              onError={setError}
            />
          )}

          {(mine.loading || signals.loading) && rows.length === 0 && (
            <div className="taskempty">{t("Reading your jobs…")}</div>
          )}

          {nothing && !mine.loading && (
            <div className="taskempty">
              {t("Nothing owed. Every job of yours has been worked.")}
            </div>
          )}

          {(dueNow.length > 0 || writtenDue.length > 0) && (
            <div className="tasksec">{t("Needs you now")}</div>
          )}
          {writtenDue.map((row) => (
            <WrittenRow
              key={row.id}
              row={row}
              people={people}
              onTick={() => void tickWritten(row)}
              onOpen={() => openWritten(row)}
              review={reviews.data?.find((review) => review.taskId === row.id)}
              onAssign={(jobId) => void assignText(row.id, jobId)}
            />
          ))}
          {dueNow.map((task) => (
            <DerivedRow
              key={task.key}
              task={task}
              onTick={() => void answer(task, null)}
              onSnooze={(until) => void answer(task, until)}
              onOpen={() => openDerived(task)}
            />
          ))}

          {(later.length > 0 || writtenLater.length > 0) && (
            <div className="tasksec">{t("When you can")}</div>
          )}
          {writtenLater.map((row) => (
            <WrittenRow
              key={row.id}
              row={row}
              people={people}
              onTick={() => void tickWritten(row)}
              onOpen={() => openWritten(row)}
              review={reviews.data?.find((review) => review.taskId === row.id)}
              onAssign={(jobId) => void assignText(row.id, jobId)}
            />
          ))}
          {later.map((task) => (
            <DerivedRow
              key={task.key}
              task={task}
              onTick={() => void answer(task, null)}
              onSnooze={(until) => void answer(task, until)}
              onOpen={() => openDerived(task)}
            />
          ))}

          {doneToday.length > 0 && (
            <>
              <div className="tasksec">{t("Done today")}</div>
              {doneToday.map((row) => (
                <div key={row.id} className="item done taskrow">
                  <span
                    className="mark"
                    role="button"
                    aria-label={t("Undo {title}", { title: row.title })}
                    onClick={() => void untick(row)}
                  >
                    {CHECK_ICON}
                  </span>
                  <span className="label" onClick={() => goTo(row.job_id)}>
                    {row.title}
                  </span>
                </div>
              ))}
            </>
          )}
        </div>
      )}
    </div>
  );
}

/** Today, as the calendar where the reader is standing reads it. */
function localToday(): string {
  const now = new Date();
  return new Date(now.getTime() - now.getTimezoneOffset() * 60_000)
    .toISOString()
    .slice(0, 10);
}

/** `days` from today, as a plain date. */
function inDays(days: number): string {
  const now = new Date();
  now.setDate(now.getDate() + days);
  return new Date(now.getTime() - now.getTimezoneOffset() * 60_000)
    .toISOString()
    .slice(0, 10);
}

/**
 * One thing the app worked out.
 *
 * The snooze is not a nicety. An item a rep has decided not to act on this week
 * would otherwise sit there for ever, and a list with something permanent in it
 * is a list nobody finishes and then nobody opens.
 */
function DerivedRow({
  task,
  onTick,
  onSnooze,
  onOpen,
}: {
  task: DerivedTask;
  onTick: () => void;
  onSnooze: (until: string) => void;
  onOpen: () => void;
}) {
  return (
    <div className={`item auto taskrow kind-${task.kind}`}>
      <span
        className="mark"
        role="button"
        aria-label={t("Done: {title}", { title: t(task.title) })}
        onClick={onTick}
      >
        {CHECK_ICON}
      </span>
      <span className="label" role="button" onClick={onOpen}>
        {t(task.title)}
        <span className="sub">{t(task.detail)}</span>
      </span>
      <button
        className="snooze"
        title={t("Put this off for a week")}
        aria-label={t("Snooze {title}", { title: t(task.title) })}
        onClick={() => onSnooze(inDays(7))}
      >
        {t("Later")}
      </button>
    </div>
  );
}

/** One thing a person typed. */
function WrittenRow({
  row,
  people,
  onTick,
  onOpen,
  review,
  onAssign,
}: {
  row: TaskRow;
  people: readonly { id: string; full_name?: string | null }[];
  onTick: () => void;
  onOpen: () => void;
  review?: TextReviewChoice | undefined;
  onAssign?: (jobId: string) => void;
}) {
  const [selectedJob, setSelectedJob] = useState("");
  const from = row.assigned_by
    ? people.find((one) => one.id === row.assigned_by)?.full_name
    : null;
  const bits = [
    row.detail,
    from ? t("from {name}", { name: from }) : null,
    row.due_on ? t("due {date}", { date: row.due_on }) : null,
  ].filter(Boolean);
  return (
    <div className={`item taskrow${review ? " review-row" : ""}`}>
      <span
        className="mark"
        role="button"
        aria-label={t("Done: {title}", { title: row.title })}
        onClick={onTick}
      >
        {CHECK_ICON}
      </span>
      <span className="label" role="button" onClick={onOpen}>
        {row.title}
        {bits.length > 0 && <span className="sub">{bits.join(" · ")}</span>}
      </span>
      {review && (
        <span className="task-review">
          <span className="task-review-context">
            <b>{review.contactName ?? t("Unknown contact")}</b>
            {review.sentAt && (
              <small>{new Date(review.sentAt).toLocaleString()}</small>
            )}
            <span className="task-review-body">
              {review.body ?? t("Text unavailable")}
            </span>
          </span>
          {review.contactId && (
            <button type="button" onClick={onOpen}>
              {t("View contact")}
            </button>
          )}
          {review.jobs.length > 0 ? (
            <>
              <select
                aria-label={t("Choose the text's job")}
                value={selectedJob}
                onChange={(event) => setSelectedJob(event.target.value)}
              >
                <option value="">{t("Choose job")}</option>
                {review.jobs.map((job) => (
                  <option key={job.id} value={job.id}>
                    #{job.job_number} · {job.stage}
                  </option>
                ))}
              </select>
              <button
                disabled={!selectedJob}
                onClick={() => onAssign?.(selectedJob)}
              >
                {t("Attach")}
              </button>
            </>
          ) : (
            <small>
              {t(
                "No available job on this contact. Review and dismiss this task if appropriate.",
              )}
            </small>
          )}
        </span>
      )}
    </div>
  );
}

/**
 * Write one down.
 *
 * Jonathon asked that anyone be able to put a task on anyone's list, so the
 * name picker offers everybody and defaults to yourself.
 */
function AddTask({
  people,
  profileId,
  onDone,
  onError,
}: {
  people: readonly { id: string; full_name?: string | null }[];
  profileId: string | null;
  onDone: () => Promise<void>;
  onError: (message: string) => void;
}) {
  const [title, setTitle] = useState("");
  const [forWhom, setForWhom] = useState(profileId ?? "");
  const [due, setDue] = useState("");
  const [busy, setBusy] = useState(false);

  async function save() {
    const text = title.trim();
    if (!text || !forWhom || busy) return;
    setBusy(true);
    try {
      await insertRow("tasks", {
        title: text,
        assigned_to: forWhom,
        assigned_by: profileId,
        due_on: due || null,
      });
      setTitle("");
      setDue("");
      await onDone();
    } catch (e) {
      onError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="taskadd">
      <input
        placeholder={t("What needs doing?")}
        aria-label={t("What needs doing?")}
        value={title}
        autoFocus
        onChange={(e) => setTitle(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === "Enter") void save();
        }}
      />
      <div className="taskaddrow">
        <select
          aria-label={t("Who it is for")}
          value={forWhom}
          onChange={(e) => setForWhom(e.target.value)}
        >
          {people.map((person) => (
            <option key={person.id} value={person.id}>
              {person.id === profileId ? t("Me") : (person.full_name ?? "—")}
            </option>
          ))}
        </select>
        <input
          type="date"
          aria-label={t("Due date")}
          value={due}
          onChange={(e) => setDue(e.target.value)}
        />
        <button
          className="primary"
          disabled={!title.trim() || busy}
          onClick={() => void save()}
        >
          {busy ? t("Saving…") : t("Add")}
        </button>
      </div>
    </div>
  );
}
